/* Outfitters product grid — sections/outfitters-live-product-grid.liquid
   All data comes from Shopify: the section is re-rendered with the Section
   Rendering API for tabs, filters, sort and the next page of products, and
   Add to Basket posts to /cart/add.js. */
(() => {
  if (window.__opgGrid) return;
  window.__opgGrid = true;

  const FILE = 'outfitters-live-product-grid';
  const ROOT_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const VIEW_KEY = 'opg:view';

  const store = {
    get(key) { try { return sessionStorage.getItem(key); } catch (_) { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch (_) {} },
  };

  /* ---------------- Section Rendering ---------------- */

  /* Inside the theme editor (and a shared preview link) the page carries the
     parameters that pick the unpublished theme. They are copied onto every
     request, otherwise Shopify answers with the live theme and the next page
     of products never arrives. */
  const PREVIEW_KEYS = ['preview_theme_id', '_ab', '_fd', '_sc', 'pb', '_pos', '_psq', '_ss', '_v'];
  const keepPreview = (u) => {
    const here = new URLSearchParams(location.search);
    PREVIEW_KEYS.forEach((k) => { if (here.has(k) && !u.searchParams.has(k)) u.searchParams.set(k, here.get(k)); });
    return u;
  };

  const withSections = (url, key) => {
    const u = new URL(url, location.origin);
    u.searchParams.delete('sections');
    u.searchParams.set('sections', key);
    return keepPreview(u);
  };

  /* Renders the listing at `url`. The section is asked for by its own id first
     (same template) and otherwise by its file name, which Shopify renders in
     that collection's context. */
  const render = async (root, url) => {
    /* Once section rendering has proved it does not answer for this grid, the
       page itself is asked straight away instead of failing twice every time. */
    const keys = root._opgPlain ? [] : [...new Set([root.dataset.renderKey, root.dataset.sectionId, FILE])].filter(Boolean);
    for (const key of keys) {
      const res = await fetch(withSections(url, key), { headers: { Accept: 'application/json' } });
      if (!res.ok) continue;
      const json = await res.json();
      const html = json && json[key];
      if (!html) continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-opg-root]');
      if (next) return { key, doc: next };
    }
    /* Last resort: load the page itself and take the grid out of it. */
    const plain = new URL(url, location.origin);
    plain.searchParams.delete('sections');
    const page = await fetch(keepPreview(plain), { headers: { Accept: 'text/html' } });
    if (page.ok) {
      const doc = new DOMParser().parseFromString(await page.text(), 'text/html');
      const next = doc.querySelector('[data-opg-root]');
      if (next) { root._opgPlain = true; return { key: root.dataset.renderKey || FILE, doc: next }; }
    }
    throw new Error('The product list could not be loaded.');
  };

  /* ---------------- cart ---------------- */

  const addToCart = async (variantId) => {
    const res = await fetch(`${ROOT_URL}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.description || body.message || 'This size could not be added.');
    /* The store header watches /cart/add.js itself (count + "Item added to your
       cart"). Without that header, tell the rest of the theme directly. */
    if (!document.querySelector('.ref-header')) {
      ['cart:refresh', 'cart:updated'].forEach((name) => document.dispatchEvent(new CustomEvent(name, { detail: { item: body } })));
      fallbackToast();
    }
    return body;
  };

  let toastTimer = 0;
  const fallbackToast = () => {
    let toast = document.querySelector('.opg-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'opg-toast';
      toast.setAttribute('role', 'status');
      toast.textContent = 'Item added to your cart';
      toast.style.cssText = 'position:fixed;right:20px;top:80px;z-index:1001;background:#fff;box-shadow:0 4px 18px rgba(0,0,0,.12);padding:14px 20px;font:700 14px/1 "Helvetica Neue",Helvetica,Arial,sans-serif;opacity:0;pointer-events:none;transition:opacity .25s';
      document.body.appendChild(toast);
    }
    toast.style.opacity = '1';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toast.style.opacity = '0'; }, 2600);
  };

  /* ---------------- cards ---------------- */

  const cardData = (card) => {
    if (card._opg) return card._opg;
    let data = { sizeIndex: -1, colorIndex: -1, single: true, current: null, variants: [] };
    try { data = JSON.parse(card.querySelector('[data-opg-data]').textContent); } catch (_) {}
    const active = card.querySelector('.opg-swatch.is-active');
    const current = data.variants.find((v) => v.id === data.current) || data.variants[0];
    data.color = active ? active.dataset.opgSwatch : (current && data.colorIndex > -1 ? current.options[data.colorIndex] : null);
    card._opg = data;
    return data;
  };

  const matching = (data) => data.variants.filter((v) => data.colorIndex < 0 || data.color == null || v.options[data.colorIndex] === data.color);

  const closePicking = (card) => {
    if (!card || !card.classList.contains('is-picking')) return;
    card.classList.remove('is-picking');
    card.querySelector('[data-opg-sizes]')?.setAttribute('aria-hidden', 'true');
  };

  const openPicking = (card) => {
    const data = cardData(card);
    const panel = card.querySelector('[data-opg-sizes]');
    const list = card.querySelector('[data-opg-size-list]');
    if (!panel || !list) return;
    const pool = matching(data);
    const sizes = [];
    data.variants.forEach((v) => { const s = v.options[data.sizeIndex]; if (s != null && !sizes.includes(s)) sizes.push(s); });
    list.textContent = '';
    sizes.forEach((size) => {
      const variant = pool.find((v) => v.options[data.sizeIndex] === size);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'opg-size';
      button.textContent = size;
      button.disabled = !variant || !variant.available;
      if (variant) button.dataset.opgVariant = variant.id;
      button.setAttribute('aria-label', `${size}${button.disabled ? ' (sold out)' : ''}`);
      list.appendChild(button);
    });
    const msg = card.querySelector('[data-opg-size-msg]');
    if (msg) { msg.hidden = true; msg.textContent = ''; }
    card.classList.add('is-picking');
    panel.setAttribute('aria-hidden', 'false');
  };

  const onBasket = async (card, button) => {
    const data = cardData(card);
    if (data.sizeIndex > -1) { openPicking(card); return; }
    const pool = matching(data).filter((v) => v.available);
    const variant = pool.find((v) => v.id === data.current) || pool[0];
    if (!variant) return;
    button.disabled = true;
    try { await addToCart(variant.id); } catch (e) { button.title = e.message; } finally { button.disabled = false; }
  };

  const onSize = async (card, button) => {
    if (button.disabled || !button.dataset.opgVariant) return;
    const msg = card.querySelector('[data-opg-size-msg]');
    button.classList.add('is-adding');
    button.disabled = true;
    try {
      await addToCart(button.dataset.opgVariant);
      closePicking(card);
    } catch (e) {
      if (msg) { msg.textContent = e.message; msg.hidden = false; }
    } finally {
      button.classList.remove('is-adding');
      button.disabled = false;
    }
  };

  const showImage = (card, swatch) => {
    const img = card.querySelector('.opg-card__img');
    if (!img || !swatch || !swatch.dataset.image) return;
    if (!img.dataset.opgOriginal) {
      img.dataset.opgOriginal = img.currentSrc || img.src;
      img.dataset.opgOriginalSet = img.getAttribute('srcset') || '';
    }
    if (img.src === swatch.dataset.image) return;
    img.classList.add('is-swapping');
    const done = () => img.classList.remove('is-swapping');
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
    if (swatch.dataset.srcset) img.setAttribute('srcset', swatch.dataset.srcset);
    else img.removeAttribute('srcset');
    img.src = swatch.dataset.image;
  };

  const selectSwatch = (card, swatch) => {
    const data = cardData(card);
    card.querySelectorAll('.opg-swatch').forEach((s) => s.classList.toggle('is-active', s === swatch));
    data.color = swatch.dataset.opgSwatch;
    const variant = matching(data).find((v) => v.available) || matching(data)[0];
    if (variant) {
      data.current = variant.id;
      card.querySelectorAll('.opg-card__link, .opg-card__title').forEach((a) => {
        const u = new URL(a.getAttribute('href'), location.origin);
        u.searchParams.set('variant', variant.id);
        a.setAttribute('href', u.pathname + u.search);
      });
    }
    showImage(card, swatch);
    if (card.classList.contains('is-picking')) openPicking(card);
  };

  const limitSwatches = (root, scope) => {
    const limit = Number(root.dataset.swatchLimit || 0);
    if (!limit) return;
    scope.querySelectorAll('.opg-card__swatches').forEach((list) => {
      [...list.children].forEach((s, i) => { s.hidden = i >= limit; });
    });
  };

  /* ---------------- grid root ---------------- */

  const init = (root) => {
    if (!root || root.dataset.opgReady === '1') return;
    root.dataset.opgReady = '1';

    const grid = root.querySelector('[data-opg-grid]');
    const status = root.querySelector('[data-opg-status]');
    const spinner = root.querySelector('[data-opg-spinner]');
    const loadingText = root.querySelector('[data-opg-loading]');
    /* How long "Loading..." stays on screen at the very least. A batch often
       comes back in a blink, and without this the line would flash by unseen:
       the next products wait for it, so every batch really is announced. */
    const MIN_BUSY = 800;
    let busyAt = 0;
    const busy = (on) => {
      if (on) busyAt = Date.now();
      if (spinner) spinner.hidden = !on;
      if (loadingText) loadingText.hidden = !on;
    };
    const settle = () => {
      const left = MIN_BUSY - (Date.now() - busyAt);
      return left > 0 ? new Promise((done) => setTimeout(done, left)) : Promise.resolve();
    };
    const end = root.querySelector('[data-opg-end]');
    const more = root.querySelector('[data-opg-more]');
    const openBtn = root.querySelector('[data-opg-open]');
    const drawer = root.querySelector('[data-opg-drawer]');
    let loadingMore = false;
    let listingGen = 0;
    const slider = root.dataset.layout === 'slider';
    const loadAll = root.dataset.loadAll === 'true';

    limitSwatches(root, root);

    /* views */
    const setView = (view, remember) => {
      const button = root.querySelector(`[data-opg-view="${view}"]`);
      if (!button) return;
      root.dataset.view = view;
      root.querySelectorAll('[data-opg-view]').forEach((b) => {
        const on = b === button;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (remember) store.set(VIEW_KEY, view);
    };
    const remembered = store.get(VIEW_KEY);
    if (remembered) setView(remembered, false);
    root.querySelectorAll('[data-opg-view]').forEach((b) => b.addEventListener('click', () => setView(b.dataset.opgView, true)));

    /* card interactions (delegated, so products loaded later work too) */
    root.addEventListener('click', (e) => {
      const card = e.target.closest('[data-opg-card]');
      if (!card) return;
      const basket = e.target.closest('[data-opg-basket]');
      const size = e.target.closest('.opg-size');
      const swatch = e.target.closest('.opg-swatch');
      if (basket) { e.preventDefault(); onBasket(card, basket); }
      else if (size) { e.preventDefault(); onSize(card, size); }
      else if (swatch) { e.preventDefault(); selectSwatch(card, swatch); }
    });
    root.addEventListener('pointerover', (e) => {
      const swatch = e.target.closest('.opg-swatch');
      if (swatch && e.pointerType === 'mouse') showImage(swatch.closest('[data-opg-card]'), swatch);
    });
    root.addEventListener('pointerout', (e) => {
      const list = e.target.closest('.opg-card__swatches');
      if (!list || list.contains(e.relatedTarget)) return;
      const card = list.closest('[data-opg-card]');
      const active = card.querySelector('.opg-swatch.is-active');
      const img = card.querySelector('.opg-card__img');
      if (active) showImage(card, active);
      else if (img && img.dataset.opgOriginal) {
        if (img.dataset.opgOriginalSet) img.setAttribute('srcset', img.dataset.opgOriginalSet);
        img.src = img.dataset.opgOriginal;
      }
    });
    /* The size panel closes when the pointer leaves the product, like the reference. */
    root.addEventListener('pointerout', (e) => {
      if (e.pointerType !== 'mouse') return;
      const card = e.target.closest('[data-opg-card]');
      if (card && !card.contains(e.relatedTarget)) closePicking(card);
    });
    /* Touch: tapping anywhere else closes an open size panel. */
    document.addEventListener('click', (e) => {
      root.querySelectorAll('[data-opg-card].is-picking').forEach((card) => { if (!card.contains(e.target)) closePicking(card); });
    });

    /* listing state */
    const setNext = (url) => {
      root.dataset.next = url || '';
      if (more) { more.hidden = !url; if (url) more.href = url; }
      if (end) end.hidden = !!url || !grid.querySelector('[data-opg-card]');
      busy(false);
    };

    const applyListing = (result, { replace }) => {
      const { key, doc } = result;
      root.dataset.renderKey = key;
      const items = [...doc.querySelectorAll('[data-opg-grid] > *')].map((n) => document.importNode(n, true));
      if (replace) { root._opgAsked?.clear(); grid.replaceChildren(...items); if (slider) grid.scrollLeft = 0; }
      else items.forEach((n) => { if (n.matches('[data-opg-card]')) grid.appendChild(n); });
      limitSwatches(root, grid);
      const title = doc.querySelector('[data-opg-title]');
      const here = root.querySelector('[data-opg-title]');
      if (replace && title && here) here.textContent = title.textContent;
      if (end) {
        const endText = doc.querySelector('[data-opg-end]');
        if (endText && endText.textContent.trim()) end.textContent = endText.textContent;
      }
      if (replace) syncFilters(doc);
      setNext(doc.dataset.next);
    };

    /* infinite scroll */
    const nearEnd = () => {
      if (slider) return grid.scrollWidth - grid.scrollLeft - grid.clientWidth < grid.clientWidth * 1.5;
      return !!status && status.getBoundingClientRect().top < innerHeight + 700;
    };
    /* Pages already asked for, so a page that keeps pointing at itself cannot
       loop for ever. Cleared whenever the whole listing is replaced. */
    const asked = new Set();
    root._opgAsked = asked;
    const loadMore = async () => {
      const next = root.dataset.next;
      if (!next || loadingMore) return;
      if (asked.has(next)) { setNext(''); return; }
      asked.add(next);
      loadingMore = true;
      const gen = listingGen;
      busy(true);
      try {
        const result = await render(root, next);
        /* a tab / filter changed the list meanwhile: these products belong to the old one */
        if (gen !== listingGen) return;
        /* the products come in once the loading line has been seen */
        await settle();
        if (gen !== listingGen) return;
        applyListing(result, { replace: false });
        syncArrows();
      } catch (e) {
        busy(false);
        asked.delete(next);
        root.classList.remove('is-infinite');
        return;
      } finally {
        if (gen === listingGen) loadingMore = false;
      }
      /* "Show all products", or still near the end (short page, tall screen,
         slider scrolled to the end)? The next batch comes by itself. */
      requestAnimationFrame(() => { if (loadAll || nearEnd()) loadMore(); });
    };
    more?.addEventListener('click', (e) => { e.preventDefault(); loadMore(); });
    /* slider: arrows step one screen of products; nearing the end loads more */
    const arrows = [...root.querySelectorAll('[data-opg-step]')];
    /* Snapping can refuse to rest at the very last pixel, so "the end" means
       there is no further page and the last product is fully in view. */
    const railEnd = () => {
      if (root.dataset.next) return false;
      const cards = grid.querySelectorAll('[data-opg-card]');
      const last = cards[cards.length - 1];
      if (!last) return true;
      /* a few pixels of slack: card widths are fractional, and anything still
         to come would stick out by a whole product, not by a pixel */
      return last.getBoundingClientRect().right - grid.getBoundingClientRect().right <= 8;
    };
    function syncArrows() {
      if (!slider || !arrows.length) return;
      arrows.forEach((a) => {
        a.disabled = a.dataset.opgStep === '-1' ? grid.scrollLeft <= 2 : railEnd();
      });
    }
    if (slider) {
      arrows.forEach((a) => a.addEventListener('click', () => {
        const card = grid.querySelector('[data-opg-card]');
        const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
        const w = card ? card.getBoundingClientRect().width + gap : grid.clientWidth;
        const perView = Math.max(1, Math.floor((grid.clientWidth + gap) / w));
        grid.scrollBy({ left: w * perView * Number(a.dataset.opgStep) });
      }));
      let raf = 0;
      grid.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => { syncArrows(); if (nearEnd()) loadMore(); });
      }, { passive: true });
      window.addEventListener('resize', syncArrows, { passive: true });
    }
    if (!slider && status) {
      root.classList.add('is-infinite');
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => { if (entries.some((en) => en.isIntersecting)) loadMore(); }, { rootMargin: '700px 0px' }).observe(status);
      }
      /* Safety net. The observer only speaks when the status line crosses the
         edge, so while it simply stays on screen the batches would stop. Every
         scroll asks again, and that is what keeps the products coming until the
         collection is finished. */
      let pumpRaf = 0;
      const pump = () => {
        cancelAnimationFrame(pumpRaf);
        pumpRaf = requestAnimationFrame(() => { if (nearEnd()) loadMore(); });
      };
      window.addEventListener('scroll', pump, { passive: true });
      window.addEventListener('resize', pump, { passive: true });
    }

    /* replace the whole listing (tabs, filters, sort) */
    const loadListing = async (url, { push }) => {
      root.classList.add('is-loading');
      listingGen += 1;
      loadingMore = false;
      try {
        const result = await render(root, url);
        applyListing(result, { replace: true });
        if (loadAll || (slider && nearEnd())) loadMore();
        syncArrows();
        const u = new URL(url, location.origin);
        root.dataset.source = u.pathname + u.search;
        /* "View All" follows the collection shown, unless a collection / link was chosen for it */
        const all = root.querySelector('[data-opg-all]');
        if (all && all.dataset.fixed !== 'true') all.href = u.pathname;
        /* Only a collection that uses this same template can be opened at
           this address later, so only then does the address bar change. */
        if (push && root.dataset.mode === 'collection' && result.key === root.dataset.sectionId) {
          history.pushState({ opg: true }, '', u.pathname + u.search);
          window.__opgPushed = true;
        }
        return true;
      } catch (e) {
        location.href = url;
        return false;
      } finally {
        root.classList.remove('is-loading');
      }
    };

    /* tabs */
    const tabs = root.querySelector('[data-opg-tabs]');
    tabs?.addEventListener('click', (e) => {
      const tab = e.target.closest('.opg__tab');
      if (!tab || !tab.dataset.opgTab || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      if (tab.classList.contains('is-active')) return;
      tabs.querySelectorAll('.opg__tab').forEach((t) => t.classList.toggle('is-active', t === tab));
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      loadListing(tab.href, { push: true });
    });

    /* ---------------- filter drawer ---------------- */
    if (drawer) {
      document.body.appendChild(drawer); /* fixed layer, never clipped by the page layout */
      drawer.style.setProperty('--opg-drawer', getComputedStyle(root).getPropertyValue('--opg-drawer'));
      drawer.style.setProperty('--opg-line', getComputedStyle(root).getPropertyValue('--opg-line'));
      root._opgDrawer = drawer;
    }
    const form = () => drawer && drawer.querySelector('[data-opg-form]');
    let openGroup = null;

    const showGroup = (id) => {
      const group = id && form()?.querySelector(`[data-opg-group="${id}"]`);
      form()?.querySelectorAll('[data-opg-group]').forEach((g) => {
        g.classList.toggle('is-open', g === group);
        g.setAttribute('aria-hidden', g === group ? 'false' : 'true');
      });
      drawer.classList.toggle('has-group', !!group);
      openGroup = group ? id : null;
    };

    /* New filter values (counts, active state) arrive with every listing. */
    function syncFilters(doc) {
      const fresh = doc.querySelector('[data-opg-form]');
      const current = form();
      if (!fresh || !current || fresh.dataset.opgFilters !== 'true') return;
      current.innerHTML = fresh.innerHTML;
      current.dataset.opgFilters = 'true';
      if (openGroup) showGroup(openGroup);
    }

    let filtersLoading = null;
    const ensureFilters = () => {
      if (!drawer || form().dataset.opgFilters === 'true') return Promise.resolve();
      /* On a normal page the filters belong to the collection, so they are
         loaded from the collection itself. */
      filtersLoading = filtersLoading || render(root, root.dataset.source)
        .then(({ doc }) => syncFilters(doc))
        .catch(() => {
          const note = form().querySelector('[data-opg-loading-filters]');
          if (note) note.textContent = 'Filters could not be loaded. Please try again.';
        })
        .finally(() => { filtersLoading = null; });
      return filtersLoading;
    };

    const openDrawer = async () => {
      if (!drawer) return;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openBtn?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('opg-lock');
      await ensureFilters();
      if (root.dataset.autoOpen === 'true' && !openGroup) {
        const first = form()?.querySelector('[data-opg-group]');
        if (first) showGroup(first.dataset.opgGroup);
      }
    };
    const closeDrawer = () => {
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openBtn?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('opg-lock');
      setTimeout(() => { if (!drawer.classList.contains('is-open')) showGroup(null); }, 420);
    };

    const listingUrl = (params) => {
      const base = new URL(root.dataset.source || location.pathname, location.origin);
      const u = new URL(base.pathname, location.origin);
      params.forEach((value, key) => { if (String(value).trim() !== '') u.searchParams.append(key, value); });
      return u.pathname + u.search;
    };

    openBtn?.addEventListener('click', openDrawer);
    if (drawer) {
      drawer.addEventListener('click', async (e) => {
        if (e.target.closest('[data-opg-close]')) { closeDrawer(); return; }
        const opener = e.target.closest('[data-opg-group-open]');
        if (opener) { showGroup(opener.dataset.opgGroupOpen); return; }
        if (e.target.closest('[data-opg-group-close]')) { showGroup(null); return; }
        if (e.target.closest('[data-opg-apply]')) {
          await ensureFilters();
          drawer.classList.add('is-busy');
          const ok = await loadListing(listingUrl(new FormData(form())), { push: true });
          drawer.classList.remove('is-busy');
          if (ok) closeDrawer();
          return;
        }
        if (e.target.closest('[data-opg-clear]')) {
          form().querySelectorAll('input').forEach((input) => {
            if (input.type === 'checkbox' || input.type === 'radio') input.checked = false;
            else input.value = '';
          });
          showGroup(null);
          drawer.classList.add('is-busy');
          await loadListing(listingUrl(new URLSearchParams()), { push: true });
          drawer.classList.remove('is-busy');
        }
      });
      drawer.addEventListener('submit', (e) => e.preventDefault());
    }
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (drawer?.classList.contains('is-open')) closeDrawer();
      root.querySelectorAll('[data-opg-card].is-picking').forEach(closePicking);
    });

    setNext(root.dataset.next);
    syncArrows();
    /* "Show all products": the rest of the collection loads straight away */
    if (loadAll || (slider && nearEnd())) loadMore();
  };

  /* Back/forward after a tab or filter change shows that page as Shopify renders it. */
  window.addEventListener('popstate', () => { if (window.__opgPushed) location.reload(); });
  document.querySelectorAll('[data-opg-root]').forEach(init);
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-opg-root]').forEach(init));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-opg-root]').forEach((root) => {
      root._opgDrawer?.remove();
      document.documentElement.classList.remove('opg-lock');
    });
  });
})();
